import React, { useState } from 'react';
import * as d3 from 'd3';
import TrackVisibility from 'react-on-screen';
import { useI18n } from '@whizz/react-i18n';
import type { HTMLAttributes } from 'react';
import type { StudentDataType, StudentType } from '../../types';
import { format } from 'd3-format';
import { Alert } from '../../../components';
import ValueHeadline from './ValueHeadline';
import StudentProgress from '../../../components/charts/student-progress/StudentProgress';
import { FIXED_AXIS } from '../../../components/charts/helpers/vars';

type Ref = HTMLDivElement;
type HTMLProps = HTMLAttributes<Ref>;

interface Props extends HTMLProps {
  /**
   * Chart data.
   */
  data: StudentType[];
  /**
   * Identification of the student selected.
   */
  studentFocused?: string | undefined;
  /**
   * Type of the chart.
   */
  chart: StudentDataType;
  /**
   * Recent or Last Weeks chart
   */
  period: 'recent' | 'last_weeks';
  /**
   * Chart title to display top right
   */
  title?: string;
  /**
   * Show gradient background
   */
  showGradientBackground?: boolean;
}

const StudentsScatterDisplay = ({
  data,
  chart,
  period,
  studentFocused,
  showGradientBackground = false,
  title,
  ...other
}: Props): JSX.Element => {

  const { translate } = useI18n();

  const [ showInfo, setShowInfo ] = useState(false);

  const getHeadLineValue = (
    studentFocused: number | undefined,
    data: StudentType[],
    chart: StudentDataType,
  ): string | null => {
    const students = data.filter((student) => studentFocused ? student.id === studentFocused : student);
    const values: number[] = [];

    students.map((student) => {
      if (student.chartsData && student.chartsData.mathsAge > 0) {
        values.push(student.chartsData[chart]);
      }
    });

    if (!values.length) {
      return '0';
    }

    const average = d3.mean(values) as number;

    switch (chart) {
      case 'progression':
        return format('.1f')(average);
      case 'qualityLearningTime':
        return format('.0f')(average);
      case 'mathsAgeImprovement':
        return format('.2f')(average);
      default:
        return null;
    }
  };

  const translateKey = `pages.HOME.${period.toUpperCase()}.${chart.toUpperCase()}`;
  const value = getHeadLineValue(studentFocused ? parseInt(studentFocused) : undefined, data, chart);

  return (
    <div {...other as HTMLProps}>
      <ValueHeadline
        title={translate(`${translateKey}.LABEL`)}
        onMoreInfo={() => setShowInfo(!showInfo)}
        value={value}
        colorValue
        highlightValue={studentFocused !== undefined}
        unit={translate(`${translateKey}.UNIT`, { count: typeof value === 'string' ? parseInt(value) : 0 })}
        description={!studentFocused ? translate('pages.HOME.PER_STUDENT') : ''}
        className={'mb-md'}
      />
      <Alert disableIcon show={showInfo} className={'my-md'}>
        <b>{translate(`${translateKey}.LONG_LABEL`)}</b> -
        {' '}{translate(`${translateKey}.INFO`)}
      </Alert>
      {title && (
        <div className={'flex justify-end mb-sm'}>
          <div className={'bg-header rounded-sm px-1 leading-snug text-xs md:text-base mr-7 md:mr-16'}>{title}</div>
        </div>
      )}
      <TrackVisibility partialVisibility>
        {({ isVisible }) => (
          <StudentProgress
            chartType={chart}
            data={data}
            showOthersData={!studentFocused}
            highlightedIds={studentFocused ? [ parseInt(studentFocused) ] : []}
            labelTranslationId={chart === 'mathsAgeImprovement' ? 'WEEK' : null}
            showGradientBackground={showGradientBackground}
            customAxis={FIXED_AXIS[period]?.[chart]}
            play={isVisible}
          />
        )}
      </TrackVisibility>
    </div>
  );
};

export default StudentsScatterDisplay;
